import { SectionBox } from '@shared/components/ui/SectionBox';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Badge } from '@shared/components/ui/badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faChevronDown,
  faCheck,
  faPlus,
  faSpinner,
  faXmark,
} from '@fortawesome/pro-light-svg-icons';
import { FC, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';
import { ConfirmModal } from '@shared/components/ui/ConfirmModal';
import type { ConfirmModalRef } from '@shared/components/ui/ConfirmModal';
import { useCrmStatus, useModifyOrderStatus } from '../../../List/hooks/useCrmStatus';
import { useConfirmOrder, useCancelOrder } from '../../../List/hooks/useOrderActions';
import { useCanManageCRM } from '@modules/CRM/shared/hooks/useCRMSettings';
import { useNotes } from '../../../List/hooks/useNotes';
import { useModifyOrderTags } from '../../../List/hooks/useTags';
import { CrmStatusModel, CRMTag } from '../../../List/models/CRM';
import { NoteModel } from '../../../List/models/Note';
import { getStatusColor } from '../../../List/config/statusConfig';
import { getTextColorForBackground } from '../../../List/utils/colorUtils';
import { NewNoteModal, NewNoteModalRef } from '../../../List/components/modals/NewNoteModal';
import { NotesModal, NotesModalRef } from '../../../List/components/modals/NotesModal';
import { TagsModal, TagsModalRef } from '../../../List/components/modals/TagsModal';

interface CRMStatusTagsNotesProps {
  orderId: number;
  orderRef: string;
  crmStatus?: CrmStatusModel[];
  statusId?: number;
  tags?: CRMTag[];
  notes?: NoteModel[];
  onUpdate?: () => void;
  updateOrder?: (updater: (prev: any) => any) => void;
}

export const CRMStatusTagsNotes: FC<CRMStatusTagsNotesProps> = ({
  orderId,
  orderRef,
  crmStatus = [],
  statusId,
  tags = [],
  notes = [],
  onUpdate,
  updateOrder,
}) => {
  return (
    <SectionBox className="bg-card-bg">
      {/* Estado */}
      <StatusSection
        orderId={orderId}
        crmStatus={crmStatus}
        statusId={statusId}
        onUpdate={onUpdate}
        updateOrder={updateOrder}
      />

      {/* Separador */}
      <div className="border-t border-card-border my-4" />

      {/* Etiquetas */}
      <TagsSection orderId={orderId} orderRef={orderRef} tags={tags} onUpdate={onUpdate} updateOrder={updateOrder} />

      {/* Separador */}
      <div className="border-t border-card-border my-4" />

      {/* Notas */}
      <NotesSection orderId={orderId} notes={notes} onUpdate={onUpdate} />
    </SectionBox>
  );
};

// Componente interno para Estado (sin SectionBox)
const StatusSection: FC<{
  orderId: number;
  crmStatus?: CrmStatusModel[];
  statusId?: number;
  onUpdate?: () => void;
  updateOrder?: (updater: (prev: any) => any) => void;
}> = ({ orderId, crmStatus, statusId, updateOrder }) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const navigate = useNavigate();
  const { statusList } = useCrmStatus();
  const modifyOrderStatusMutation = useModifyOrderStatus();
  const confirmOrderMutation = useConfirmOrder();
  const cancelOrderMutation = useCancelOrder();
  const confirmOrderModalRef = useRef<ConfirmModalRef>(null);
  const cancelOrderModalRef = useRef<ConfirmModalRef>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const hasCrmStatus = crmStatus && crmStatus.length > 0;
  const currentStatus = crmStatus?.[0];
  const currentStatusId = currentStatus?.id ?? null;
  // Verificar si el estado actual es fijo (sin team_id)
  const isFixedStatus = currentStatus && currentStatus.team_id == null;

  // Verificar si el usuario puede gestionar CRM
  const { canManage } = useCanManageCRM();

  // Deshabilitar acciones si no puede gestionar o si el estado es fijo
  const isDisabled = !canManage || isFixedStatus;

  const handleStatusChange = async (crmStatusId: number | null) => {
    // Actualizar localmente de forma optimista
    if (updateOrder) {
      const newStatusObj = statusList.find((s) => s.id === crmStatusId);
      const newCrmStatus = newStatusObj ? [newStatusObj] : [];
      updateOrder((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          crm_status: newCrmStatus,
          status_id: crmStatusId,
        };
      });
    }

    setIsProcessing(true);
    try {
      await modifyOrderStatusMutation.mutateAsync({ order_id: orderId, crm_status_id: crmStatusId });
      toast({
        title: t('table.statusChangeSuccess', 'Estado actualizado'),
        description: crmStatusId
          ? t('table.statusChangeSuccessDescription', 'El estado del pedido se ha actualizado correctamente.')
          : t('table.statusRemovedDescription', 'Se ha quitado el estado del pedido.'),
        variant: 'default',
      });
      // No llamar a onUpdate para evitar refetch
    } catch (error: any) {
      // Revertir cambio optimista en caso de error
      if (updateOrder) {
        updateOrder((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            crm_status: crmStatus || [],
            status_id: statusId,
          };
        });
      }
      toast({
        title: t('table.statusChangeError', 'Error al cambiar estado'),
        description: error?.response?.data?.message ?? t('toasts.error_title', 'Error'),
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirm = () => {
    confirmOrderModalRef.current?.open(
      async () => {
        setIsProcessing(true);
        try {
          await confirmOrderMutation.mutateAsync(orderId);
          toast({
            title: t('actions.confirmSuccess.title', 'Operación exitosa'),
            description: t('actions.confirmSuccess.description', 'El pedido se ha procesado correctamente.'),
            variant: 'default',
          });
          navigate('/orders/crm');
        } catch (error: any) {
          toast({
            title: t('actions.confirmError.title', 'Error al confirmar pedido'),
            description: error?.response?.data?.message ?? t('actions.confirmError.description', 'Error inesperado.'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessing(false);
        }
      },
      undefined,
      t('actions.confirmConfirm.title', 'Confirmar pedido'),
      t('actions.confirmConfirm.message', '¿Estás seguro de que deseas confirmar este pedido?'),
    );
  };

  const handleCancel = () => {
    cancelOrderModalRef.current?.open(
      async () => {
        setIsProcessing(true);
        try {
          await cancelOrderMutation.mutateAsync(orderId);
          toast({
            title: t('actions.cancelSuccess.title', 'Operación exitosa'),
            description: t('actions.cancelSuccess.description', 'El pedido se ha procesado correctamente.'),
            variant: 'default',
          });
          navigate('/orders/crm');
        } catch (error: any) {
          toast({
            title: t('actions.cancelError.title', 'Error al cancelar pedido'),
            description: error?.response?.data?.message ?? t('actions.cancelError.description', 'Error inesperado.'),
            variant: 'destructive',
          });
        } finally {
          setIsProcessing(false);
        }
      },
      undefined,
      t('actions.cancelConfirm.title', 'Cancelar pedido'),
      t('actions.cancelConfirm.message', '¿Estás seguro de que deseas cancelar este pedido?'),
    );
  };

  const triggerBadge = !hasCrmStatus ? (
    <Badge className="text-xs bg-bg-error text-error rounded-full px-3 py-2 flex items-center justify-between gap-1.5 w-full">
      <span>{t('table.noStatus', 'Sin estado')}</span>
      <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 opacity-70" />
    </Badge>
  ) : (
    <Badge
      className={`text-xs rounded-full px-3 py-2 flex items-center justify-between gap-1.5 w-full ${!currentStatus?.color ? getStatusColor(statusId || 0) : ''}`}
      style={
        currentStatus?.color
          ? {
              backgroundColor: currentStatus.color,
              color: getTextColorForBackground(currentStatus.color),
            }
          : undefined
      }
    >
      <span>{currentStatus?.name ?? `Estado ${statusId}`}</span>
      <FontAwesomeIcon icon={faChevronDown} className="w-3 h-3 opacity-80" />
    </Badge>
  );

  return (
    <>
      <h2 className="text-lg font-bold">{t('details.status.title', 'Estado')}</h2>
      <div className="pt-4 space-y-4">
        {statusList.length > 0 && !isDisabled ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="cursor-pointer hover:opacity-90 transition-opacity w-full"
                disabled={isProcessing || modifyOrderStatusMutation.isPending}
              >
                {triggerBadge}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-h-[280px] overflow-y-auto">
              {statusList
                .filter((status) => status.team_id != null) // Solo mostrar estados con team_id (editables)
                .map((status) => {
                  const isSelected = currentStatusId === status.id;
                  return (
                    <DropdownMenuItem
                      key={status.id}
                      onClick={() => handleStatusChange(status.id)}
                      disabled={isProcessing || modifyOrderStatusMutation.isPending}
                    >
                      <span className="inline-flex items-center gap-2 w-full">
                        {isSelected ? (
                          <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5 text-primary shrink-0" />
                        ) : (
                          <span className="w-3.5 shrink-0" />
                        )}
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs min-w-[4rem] text-center ${!status.color ? 'bg-card-border text-text' : ''}`}
                          style={
                            status.color
                              ? {
                                  backgroundColor: status.color,
                                  color: getTextColorForBackground(status.color),
                                }
                              : undefined
                          }
                        >
                          {status.name}
                        </span>
                      </span>
                    </DropdownMenuItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <div className="w-full">{triggerBadge}</div>
        )}

        <div className="flex flex-row gap-2 pt-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleConfirm}
            disabled={isDisabled || isProcessing || confirmOrderMutation.isPending}
            className="group flex-1 border-success text-success hover:bg-success hover:text-white bg-bg-success"
          >
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="w-3 h-3 text-success group-hover:text-white transition-colors"
            />
            {t('actions.confirm', 'Confirmar')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isDisabled || isProcessing || cancelOrderMutation.isPending}
            className="group flex-1 border-error text-error hover:bg-error hover:text-white bg-bg-error"
          >
            <FontAwesomeIcon
              icon={faCircleXmark}
              className="w-3 h-3 text-error group-hover:text-white transition-colors"
            />
            {t('actions.cancel', 'Cancelar')}
          </Button>
        </div>
      </div>
      <ConfirmModal ref={confirmOrderModalRef} />
      <ConfirmModal ref={cancelOrderModalRef} />
    </>
  );
};

// Componente interno para Etiquetas (sin SectionBox)
const TagsSection: FC<{
  orderId: number;
  orderRef: string;
  tags: CRMTag[];
  onUpdate?: () => void;
  updateOrder?: (updater: (prev: any) => any) => void;
}> = ({ orderId, orderRef, tags, onUpdate, updateOrder }) => {
  const { t } = useTranslation('crmModule');
  const { toast } = useToast();
  const tagsModalRef = useRef<TagsModalRef>(null);
  const removeTagConfirmRef = useRef<ConfirmModalRef>(null);
  const modifyOrderTagsMutation = useModifyOrderTags();
  const [removingTagId, setRemovingTagId] = useState<number | null>(null);

  const handleOpenModal = () => {
    tagsModalRef.current?.open(orderId, orderRef, tags);
  };

  const handleTagSuccess = () => {
    onUpdate?.();
  };

  const performTagRemove = async (tagId: number) => {
    // Actualizar localmente de forma optimista
    if (updateOrder) {
      updateOrder((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          tags: (prev.tags || []).filter((tag: CRMTag) => tag.id !== tagId),
        };
      });
    }

    setRemovingTagId(tagId);
    try {
      await modifyOrderTagsMutation.mutateAsync({
        order_id: orderId,
        detach_tags: [tagId],
      });
      toast({
        title: t('tags.removeSuccess.title', 'Etiqueta eliminada'),
        description: t('tags.removeSuccess.description', 'La etiqueta se ha quitado del pedido.'),
        variant: 'default',
      });
      // No llamar a onUpdate para evitar refetch
    } catch (error: any) {
      // Revertir cambio optimista en caso de error
      if (updateOrder) {
        updateOrder((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            tags: tags, // Restaurar tags originales
          };
        });
      }
      toast({
        title: t('tags.removeError.title', 'Error al eliminar etiqueta'),
        description:
          error?.response?.data?.message || error?.message || t('tags.removeError.description', 'Error inesperado.'),
        variant: 'destructive',
      });
    } finally {
      setRemovingTagId(null);
    }
  };

  const handleRemoveTagClick = (tag: CRMTag) => {
    removeTagConfirmRef.current?.open(
      async () => {
        await performTagRemove(tag.id);
      },
      undefined,
      t('tags.removeConfirm.title', 'Desasignar etiqueta'),
      t('tags.removeConfirm.message', '¿Desasignar la etiqueta del pedido?').replace('{{name}}', tag.name),
    );
  };

  return (
    <>
      <h2 className="text-lg font-bold">{t('details.tags.title', 'Etiquetas')}</h2>
      <div className="pt-4">
        {tags.length === 0 ? (
          <p className="text-sm text-complementary-text mb-2">
            {t('details.tags.empty', 'Aún no se han agregado etiquetas para este pedido.')}
          </p>
        ) : null}
        <div className="flex flex-wrap items-center gap-2">
          {tags.map((tag) => (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-sm border"
              style={
                tag.color
                  ? {
                      backgroundColor: tag.color,
                      color: getTextColorForBackground(tag.color),
                      borderColor: tag.color,
                    }
                  : {
                      backgroundColor: 'var(--wrh-card-border)',
                      color: 'var(--wrh-text)',
                      borderColor: 'var(--wrh-card-border)',
                    }
              }
            >
              <span>{tag.name}</span>
              {removingTagId === tag.id ? (
                <span className="flex items-center justify-center w-4 h-4 ml-0.5" aria-hidden>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="w-2.5 h-2.5 animate-spin"
                    style={{ color: tag.color ? getTextColorForBackground(tag.color) : 'var(--wrh-text)' }}
                  />
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => handleRemoveTagClick(tag)}
                  disabled={modifyOrderTagsMutation.isPending}
                  className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-hover-press transition-colors ml-0.5"
                  data-testid={`remove-tag-${orderId}-${tag.id}`}
                  aria-label={t('tags.remove', 'Eliminar etiqueta')}
                >
                  <FontAwesomeIcon
                    icon={faXmark}
                    className="w-2.5 h-2.5"
                    style={{ color: tag.color ? getTextColorForBackground(tag.color) : 'var(--wrh-text)' }}
                  />
                </button>
              )}
            </span>
          ))}
          <button
            type="button"
            onClick={handleOpenModal}
            className="flex items-center justify-center h-6 px-3 rounded-full border border-gray-400 bg-gray-200 hover:bg-gray-300 dark:border-card-border dark:bg-transparent dark:hover:bg-hover-press transition-colors"
            data-testid="add-tag-button"
            aria-label={t('tags.add', 'Añadir etiqueta')}
          >
            <FontAwesomeIcon icon={faPlus} className="w-3.5 h-3.5 text-gray-600 dark:text-icons" />
          </button>
        </div>
      </div>
      <TagsModal ref={tagsModalRef} onSuccess={handleTagSuccess} />
      <ConfirmModal ref={removeTagConfirmRef} />
    </>
  );
};

// Componente interno para Notas (sin SectionBox)
const NotesSection: FC<{
  orderId: number;
  notes: NoteModel[];
  onUpdate?: () => void;
}> = ({ orderId, notes: initialNotes, onUpdate }) => {
  const { t } = useTranslation('crmModule');
  const newNoteModalRef = useRef<NewNoteModalRef>(null);
  const notesModalRef = useRef<NotesModalRef>(null);
  const { canManage } = useCanManageCRM();

  const { notes, isLoading, refetch } = useNotes(orderId);

  const displayNotes = notes.length > 0 ? notes : initialNotes;
  const mostRecentNote = displayNotes.length > 0 ? displayNotes[0] : null;

  const canMutateNotes = canManage;

  const handleAddNote = () => {
    newNoteModalRef.current?.open(orderId);
  };

  const handleViewMore = () => {
    notesModalRef.current?.open(orderId);
  };

  const handleNoteSuccess = () => {
    refetch();
    onUpdate?.();
  };

  return (
    <>
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-bold">{t('details.notes.title', 'Notas')}</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={handleAddNote}
          disabled={!canMutateNotes}
          className="bg-background"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2 !w-3 !h-3" />
          {t('notes.new', 'Añadir')}
        </Button>
      </div>

      {isLoading ? (
        <div className="pt-4">
          <p className="text-sm text-complementary-text">{t('loading.message', 'Cargando...')}</p>
        </div>
      ) : mostRecentNote ? (
        <div className="pt-4 space-y-3">
          <p className="text-xs text-complementary-text font-semibold">
            {t('details.notes.mostRecent', 'Más Reciente')}
          </p>
          <div className="bg-background border-card-border border rounded-lg p-4">
            <p className="text-sm text-text mb-3 whitespace-pre-wrap">{mostRecentNote.content}</p>
            <div className="flex justify-between items-center gap-2 text-xs text-complementary-text">
              <span>{mostRecentNote.creator?.name || t('notes.unknownAuthor', 'Usuario desconocido')}</span>
              <span>
                {(() => {
                  const date = new Date(mostRecentNote.created_at);
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const year = date.getFullYear();
                  return `${hours}:${minutes} - ${day}/${month}/${year}`;
                })()}
              </span>
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleViewMore}
              className="text-sm text-title hover:opacity-80 transition-opacity cursor-pointer"
              data-testid="notes-view-more-button"
            >
              {t('details.notes.viewMore', 'Ver más')}
            </button>
          </div>
        </div>
      ) : (
        <div className="pt-4">
          <p className="text-sm text-complementary-text">
            {t('notes.empty.description', 'Aún no se han agregado notas para este pedido.')}
          </p>
        </div>
      )}
      <NewNoteModal ref={newNoteModalRef} onSuccess={handleNoteSuccess} />
      <NotesModal ref={notesModalRef} onSuccess={handleNoteSuccess} />
    </>
  );
};
